<script setup lang="ts">
import { onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useSession } from './stores/session'
import { PUBLIC_ROUTES } from './router'
import Landing from './views/Landing.vue'
import Toasts from './components/Toasts.vue'
import TransferStatus from './components/TransferStatus.vue'
import NowPlaying from './components/NowPlaying.vue'
import PartyPlayer from './components/PartyPlayer.vue'
import IncomingCall from './components/IncomingCall.vue'
import CallMini from './components/CallMini.vue'
import NotificationsPrompt from './components/NotificationsPrompt.vue'
import { bottomInset } from './lib/bottomBar'
import { installOnePlayer } from './lib/onePlayer'
import { useParty } from './stores/party'

const session = useSession()
const route = useRoute()

onMounted(() => session.boot())
installOnePlayer(() => void useParty().leave())

// A page's CSP is fixed when it loads, so a storage bucket added since then is blocked
// until a reload (HRTH-125).
let toldReload = false
document.addEventListener('securitypolicyviolation', (e) => {
  if (toldReload || e.effectiveDirective !== 'connect-src' || !/^https?:/.test(e.blockedURI)) return
  if (new URL(e.blockedURI).origin === location.origin) return
  toldReload = true
  session.toast('Storage was added since this page opened: reload to reach it', 'error')
})
</script>

<template>
  <div class="h-full" :style="{ paddingBottom: `${bottomInset}px` }">
    <div v-if="session.status === 'loading'" class="flex h-full items-center justify-center" data-testid="loading">
      <div class="flex w-48 flex-col gap-3">
        <span class="mono flex justify-between tracking-[0.4em] text-text-primary">HRTH<span class="tracking-normal text-text-muted">v0</span></span>
        <div class="relative h-px overflow-hidden bg-border"><div class="absolute inset-y-0 w-1/4 animate-scan bg-ink" /></div>
        <span class="mono text-text-muted uppercase">Unlocking keys…</span>
      </div>
    </div>
    <div v-else-if="session.status === 'error'" class="flex h-full items-center justify-center p-6">
      <div class="max-w-md border border-signal p-6">
        <div class="mono mb-3 text-signal">ERR · BOOT</div>
        <h1 class="mb-2 text-2xl font-light">Something went wrong</h1>
        <p class="text-sm text-text-muted">{{ session.bootError }}</p>
        <button class="btn-primary mt-4" @click="session.boot()">Try again</button>
      </div>
    </div>
    <div v-else-if="session.status === 'other-tab'" class="flex h-full items-center justify-center p-6" data-testid="other-tab">
      <div class="max-w-sm border border-border-strong p-6">
        <div class="mono mb-3 text-text-muted">LOCK · KEYS IN USE</div>
        <h1 class="mb-2 text-2xl font-light">hrth is open in another tab</h1>
        <p class="text-sm text-text-muted">
          Your encryption keys can only be used in one tab at a time on this device.
        </p>
        <button class="btn-primary mt-4" data-testid="use-here" @click="session.useHere()">Use here instead</button>
      </div>
    </div>
    <router-view v-else-if="session.status === 'ready' || PUBLIC_ROUTES.includes(String(route.name))" />
    <Landing v-else />
    <NowPlaying v-if="session.status === 'ready'" />
    <PartyPlayer v-if="session.status === 'ready'" />
    <CallMini v-if="session.status === 'ready'" />
    <IncomingCall v-if="session.status === 'ready'" />
    <NotificationsPrompt v-if="session.status === 'ready'" />
    <Toasts />
    <TransferStatus />
  </div>
</template>
